import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Check, Sparkles, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [plans, setPlans] = useState(null);
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    }, []);

    const checkout = async (planId) => {
        if (!user) {
            nav("/auth?next=/pricing");
            return;
        }
        setBusy(planId);
        try {
            const res = await api.post("/payments/initiate", { plan: planId });
            // Auto-submit form to PayU
            const f = document.createElement("form");
            f.method = "POST";
            f.action = res.data.action_url;
            Object.entries(res.data.form).forEach(([k, v]) => {
                const i = document.createElement("input");
                i.type = "hidden";
                i.name = k;
                i.value = v;
                f.appendChild(i);
            });
            document.body.appendChild(f);
            f.submit();
        } catch (e) {
            toast.error("Could not start checkout", { description: e?.response?.data?.detail || e.message });
            setBusy(null);
        }
    };

    if (!plans) {
        return (
            <div className="min-h-screen bg-white">
                <Header />
                <div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            <Header />
            <main className="max-w-5xl mx-auto px-6 py-16 sm:py-24">
                <div className="text-center max-w-2xl mx-auto fade-in-up">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-widest" data-testid="pricing-eyebrow">Pricing</p>
                    <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900" data-testid="pricing-title">
                        Simple plans, real value.
                    </h1>
                    <p className="mt-4 text-lg text-slate-500">
                        Start free. Upgrade only when you need more minutes.
                    </p>
                </div>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
                    {/* Free */}
                    <PlanCard
                        testId="plan-free"
                        title="Free"
                        sub="Perfect for trying out"
                        priceUsd={0}
                        priceInr={0}
                        limit="20 min / month"
                        features={[
                            "Speaker labels & timestamps",
                            "99+ languages",
                            "TXT / SRT / JSON export",
                            "Public share links",
                        ]}
                        cta={user?.subscription_status === "free" ? "Current plan" : "Get started"}
                        ctaDisabled={user?.subscription_status === "free"}
                        onClick={() => nav(user ? "/app" : "/auth")}
                    />

                    {/* Monthly */}
                    <PlanCard
                        testId="plan-monthly"
                        highlight
                        title="Pro Monthly"
                        sub={plans.monthly.label}
                        priceUsd={plans.monthly.price_usd}
                        priceInr={plans.monthly.price_inr}
                        period="/mo"
                        limit={plans.monthly.label}
                        features={[
                            "10 hours of transcription / month",
                            "Audio generation (TTS) unlocked",
                            "Voice cloning unlocked",
                            "Auto-renews monthly via PayU",
                            "Cancel anytime",
                        ]}
                        cta={busy === "monthly" ? "Redirecting…" : "Upgrade — Pay with PayU"}
                        ctaBusy={busy === "monthly"}
                        ctaDisabled={user?.subscription_plan === "monthly"}
                        onClick={() => checkout("monthly")}
                    />

                    {/* Yearly */}
                    <PlanCard
                        testId="plan-yearly"
                        title="Pro Yearly"
                        sub="Best value"
                        badge="Limited time"
                        listUsd={plans.yearly.list_price_usd}
                        priceUsd={plans.yearly.price_usd}
                        priceInr={plans.yearly.price_inr}
                        listInr={plans.yearly.list_price_inr}
                        period="/yr"
                        limit={plans.yearly.label}
                        features={[
                            "Unlimited transcription",
                            "Priority processing",
                            "Save 36% vs monthly",
                            "All free + Pro features",
                        ]}
                        cta={busy === "yearly" ? "Redirecting…" : "Upgrade — Pay with PayU"}
                        ctaBusy={busy === "yearly"}
                        ctaDisabled={user?.subscription_plan === "yearly"}
                        onClick={() => checkout("yearly")}
                    />
                </div>

                <p className="mt-10 text-center text-sm text-slate-400">
                    Prices charged in INR via PayU. Conversion: $1 = ₹{plans.currency_rate_inr}. All plans renew until cancelled.
                </p>

                <div className="mt-16 text-center">
                    <Link to="/" className="text-sm text-slate-500 hover:text-slate-900 transition-colors">← Back home</Link>
                </div>
            </main>
        </div>
    );
}

function PlanCard({ testId, title, sub, priceUsd, priceInr, listUsd, listInr, period = "", limit, features, cta, ctaBusy, ctaDisabled, onClick, badge, highlight }) {
    return (
        <div
            className={`relative rounded-2xl border bg-white p-7 transition-all duration-200 ${
                highlight
                    ? "border-indigo-200 shadow-[0_8px_30px_rgba(79,70,229,0.08)]"
                    : "border-slate-200 shadow-[0_2px_12px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:border-slate-300"
            }`}
            data-testid={testId}
        >
            {badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs font-medium text-amber-700">
                    <Sparkles className="w-3 h-3" strokeWidth={2} />
                    {badge}
                </span>
            )}
            {highlight && !badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-200 px-3 py-1 text-xs font-medium text-indigo-700">
                    <Zap className="w-3 h-3" strokeWidth={2} />
                    Popular
                </span>
            )}

            <h3 className="text-lg font-semibold tracking-tight text-slate-900">{title}</h3>
            <p className="mt-1 text-sm text-slate-500">{sub}</p>

            <div className="mt-5 flex items-baseline gap-2">
                {listUsd && listUsd !== priceUsd && (
                    <span className="text-base text-slate-400 line-through">${listUsd}</span>
                )}
                <span className="text-4xl font-semibold tracking-tight text-slate-900">${priceUsd}</span>
                <span className="text-sm text-slate-500">{period}</span>
            </div>
            <div className="mt-1 flex items-baseline gap-2 text-xs text-slate-400">
                {listInr && listInr !== priceInr && (
                    <span className="line-through">₹{listInr}</span>
                )}
                <span className="font-mono">₹{priceInr} INR</span>
            </div>

            <ul className="mt-6 space-y-2.5">
                {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" strokeWidth={2.25} />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>

            <button
                onClick={onClick}
                disabled={ctaDisabled || ctaBusy}
                className={`mt-7 w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    highlight
                        ? "bg-indigo-600 text-white hover:bg-indigo-700"
                        : "bg-slate-900 text-white hover:bg-slate-800"
                }`}
                data-testid={`${testId}-cta`}
            >
                {ctaBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                {cta}
            </button>
        </div>
    );
}
